import { useEffect, useRef } from "react";

const assurances = [
  { icon: "ri-time-line", text: "Intervention 24h" },
  { icon: "ri-file-list-3-line", text: "Devis & déplacement gratuits" },
  { icon: "ri-price-tag-3-line", text: "Toutes marques" },
  { icon: "ri-shield-check-line", text: "Garantie 10 ans" },
];

export default function Hero() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.querySelectorAll("[data-animate]").forEach((child, i) => {
              (child as HTMLElement).style.animationDelay = `${i * 0.1}s`;
              child.classList.add("animate-fade-in-up");
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={heroRef} className="relative bg-plum pt-16 md:pt-20 pb-8 md:pb-0 min-h-[480px] md:min-h-[580px] overflow-hidden">
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-[1280px] mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-0">
          {/* Text block left */}
          <div className="w-full lg:w-[55%] py-8 md:py-12 lg:pr-12">
            <p data-animate className="font-serif italic text-rose-200 text-sm md:text-base mb-4 opacity-0">
              Spécialiste crémone de fenêtre Strasbourg
            </p>
            <h1 data-animate className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.4rem] font-bold text-white leading-tight mb-6 opacity-0">
              Réparation et remplacement de{" "}
              <span className="font-serif italic text-coral">crémone de fenêtre</span>{" "}
              à Strasbourg
            </h1>
            <p data-animate className="text-rose-50 text-base md:text-lg leading-relaxed mb-8 max-w-xl opacity-0">
              Crémone cassée, fenêtre qui ne verrouille plus ? Nos techniciens remplacent le mécanisme chez vous.
            </p>
            <div data-animate className="flex flex-col sm:flex-row gap-4 mb-10 opacity-0">
              <a
                href="tel:0374471036"
                className="inline-flex items-center justify-center gap-2 bg-coral text-white px-6 py-4 rounded-xl font-semibold text-base whitespace-nowrap hover:bg-coral-light transition-colors"
              >
                <i className="ri-phone-line" />
                Appeler le 03 74 47 10 36
              </a>
              <a
                href="#devis"
                className="inline-flex items-center justify-center gap-2 border border-white/60 text-white px-6 py-4 rounded-xl font-semibold text-base whitespace-nowrap hover:bg-white hover:text-plum transition-colors"
              >
                Devis gratuit
                <i className="ri-arrow-down-line" />
              </a>
            </div>
            <div data-animate className="grid grid-cols-2 gap-4 max-w-md opacity-0">
              {assurances.map((a) => (
                <div key={a.text} className="flex items-center gap-2 text-white/90">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10">
                    <i className={`${a.icon} text-coral`} />
                  </div>
                  <span className="text-xs md:text-sm font-medium">{a.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Image right */}
          <div className="w-full lg:w-[45%] flex justify-center lg:justify-end">
            <div className="relative w-full max-w-md lg:max-w-lg xl:max-w-xl">
              <div className="rounded-2xl overflow-hidden border-2 border-white/20 shadow-2xl">
                <img
                  src="https://static.readdy.ai/image/60b7a1bdc124301cce40c5bb19c672c9/270072d9fe20dda93c94f272f4415ced.jpeg"
                  alt="Crémone de fenêtre démontée en cours de remplacement à Strasbourg"
                  className="w-full h-[340px] md:h-[440px] lg:h-[520px] object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 md:-left-8 bg-white rounded-xl px-4 py-3 shadow-lg flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-coral/10 flex items-center justify-center">
                  <i className="ri-tools-line text-coral text-lg" />
                </div>
                <div>
                  <p className="text-plum-dark font-bold text-sm">90% réparés</p>
                  <p className="text-plum-dark/70 text-xs">Sans changer la fenêtre</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
        }
      `}</style>
    </section>
  );
}